import { useState, useContext, useEffect, useMemo } from 'react';
import Layout from '../../components/layouts/freshest/FreshestLayout';
import PropTypes from 'prop-types';
import { Licensing, NotificationContext, InternalLink } from '@mdc/ui';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { metadefenderService, getLocalisedOpswatLink } from '@mdc/services';
import { NOTIFICATION_TYPES } from '@mdc/constants';
import { OutboundLink } from 'gatsby-plugin-google-analytics';

import plans from '@mdc/ui/src/licensing/plans.data';
import faqs from '@mdc/ui/src/licensing/faqs.data';

const LicensingTemplate = ({ data }) => {
    const { t, ready } = useTranslation();
    const [nrOfEngines, setNrOfEngines] = useState(null);
    const { notify } = useContext(NotificationContext);
    const url = 'status/engines-cloud';

    useEffect(() => {
        (async () => {
            try {
                const [enginesPromise] = await metadefenderService.url.getUrlResults({ address: url });
                const enginesNumber = await enginesPromise;
                if (enginesNumber) {
                    setNrOfEngines(enginesNumber.data);
                }
            } catch (error) {
                notify({
                    message: t('A server error occurred, and your request could not be completed. Please refresh the page'),
                    type: NOTIFICATION_TYPES.CRITICAL
                });
            }
        })();
    }, [url]);

    const domEnginesList = (
        <Row className="enginesList">
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.aegisLab.fluid} alt='Aegis Lab'
                        onStartLoad={() => {
                            const elems = document.querySelectorAll('.gatsby-image-wrapper');
                            [].forEach.call(elems, function (el) {
                                el.classList.remove('gatsby-image-wrapper');
                                el.classList.add('mdc-image-wrapper');
                            });
                        }} />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.anhLab.fluid} alt='AhnLab' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.avira.fluid} alt='Avira' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.bitDefender.fluid} alt='Bitdefender' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.clamAv.fluid} alt='ClamAV' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.comodoLicensing.fluid} alt='Comodo Licensing' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.crowdStrikeFalconMl.fluid} alt='CrowdStrike Falcon ML' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.cyren.fluid} alt='Cyren' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.emsiSoft.fluid} alt='Emsisoft' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.filsecLab.fluid} alt='Filseclab' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.ikarus.fluid} alt='IKARUS' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.kaspersky.fluid} alt='Kaspersky' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.k7.fluid} alt='K7' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.mcafee.fluid} alt='McAfee' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.nanoAv.fluid} alt='NANOAV' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.quickHeal.fluid} alt='Quick Heal' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.rocketCyber.fluid} alt='RocketCyber' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.tachyon.fluid} alt='tachyon' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.virItExplorer.fluid} alt='VirItExplorer' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.virIt.fluid} alt='VirIt' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.webRoot.fluid} alt='WebRoot' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.windowsDefender.fluid} alt='Microsoft Defender' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.xVirusPersonalGuard.fluid} alt='Xvirus Anti-Malware' />
                </Col>
            </Col>
            <Col xs={6} sm={3} md={2}>
                <Col xs={12}>
                    <Img fluid={data.zillya.fluid} alt='Zillya!' />
                </Col>
            </Col>
        </Row>
    );

    const planButtons = {
        'free': <InternalLink to='/'>{t('Try now')}</InternalLink>,
        'commercial': <InternalLink to='/store'>{t('Buy now')}</InternalLink>,
        'enterprise': <OutboundLink href={getLocalisedOpswatLink('https://www.opswat.com/contact')} rel="noopener noreferrer" target="_blank">{ t('Request an evaluation') }</OutboundLink>
    };

    const licensing = useMemo(() => {
        return  <Licensing plans={plans(t)} faqs={faqs} domEnginesList={domEnginesList} licensingIcon={data.licensingIcon.publicURL} planButtons={planButtons} nrOfEngines={nrOfEngines}/>;
    }, [nrOfEngines, t]);


    if (!ready) {
        return;
    }

    return (
        <Layout pageTitle="Licensing" className='licensingTemplate'>
            {licensing}
        </Layout>
    );
};

LicensingTemplate.propTypes = {
    data: PropTypes.object
};

export default LicensingTemplate;

export const engineImages = graphql`
query engineImages {
    aegisLab: imageSharp(resize: {originalName: {eq: "aegis-lab.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    anhLab: imageSharp(resize: {originalName: {eq: "ahn-lab.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    avira: imageSharp(resize: {originalName: {eq: "avira.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    bitDefender: imageSharp(resize: {originalName: {eq: "bitdefender.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    clamAv: imageSharp(resize: {originalName: {eq: "clamav.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    comodoLicensing: imageSharp(resize: {originalName: {eq: "comodo-licensing.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    crowdStrikeFalconMl: imageSharp(resize: {originalName: {eq: "crowdstrikefalconml.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    cyren: imageSharp(resize: {originalName: {eq: "cyren.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    emsiSoft: imageSharp(resize: {originalName: {eq: "emsisoft.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    filsecLab: imageSharp(resize: {originalName: {eq: "filseclab.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    ikarus: imageSharp(resize: {originalName: {eq: "ikarus.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    kaspersky: imageSharp(resize: {originalName: {eq: "kaspersky.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    k7: imageSharp(resize: {originalName: {eq: "k7.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    mcafee: imageSharp(resize: {originalName: {eq: "mcafee.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    webRoot: imageSharp(resize: {originalName: {eq: "webroot.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    windowsDefender: imageSharp(resize: {originalName: {eq: "windowsdefender.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    nanoAv: imageSharp(resize: {originalName: {eq: "nanoav.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    quickHeal: imageSharp(resize: {originalName: {eq: "quickheal.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    rocketCyber: imageSharp(resize: {originalName: {eq: "rocketcyber.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    tachyon: imageSharp(resize: {originalName: {eq: "tachyon.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    virItExplorer: imageSharp(resize: {originalName: {eq: "viritexplorer.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    virIt: imageSharp(resize: {originalName: {eq: "virit.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    xVirusPersonalGuard: imageSharp(resize: {originalName: {eq: "xviruspersonalguard.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    zillya: imageSharp(resize: {originalName: {eq: "zillya.png"}}) {
        fluid(quality: 100, maxWidth: 100) {
            ...GatsbyImageSharpFluid
        }
    }
    licensingIcon: file(relativePath: {eq: "icons/licensing.svg"}) {
        publicURL
    }
  }
`;
